import React, {
  FunctionComponent,
  SyntheticEvent,
  useEffect,
  useState,
} from 'react';
import {
  map,
  includes,
  path,
  find,
  propEq,
  without,
  concat,
  prop,
} from 'ramda';
import {
  Button,
  Header,
  Form,
  Grid,
  Card,
  CheckboxProps,
  Checkbox,
} from 'semantic-ui-react';
import moment from 'moment';
import { match } from 'react-router';
import classnames from 'classnames';

import { Controller, FormProvider, useForm } from 'react-hook-form';
import { Option } from 'components/FormSelect/FormSelect.helpers';

import { isValidReport, Report, Meta, getAllReports } from './Report.helper';
import { Merchant } from 'hooks/useMerchants';

import mock from './Report.mock';
import { Styles } from './Report';
import StatSig from './components/ReportBase/components/StatSig';

type SimpleDropdownOption = Omit<Option, 'value'> & { value: string };
interface Props {
  merchants: Merchant[];
  merchantDropdownOptions: SimpleDropdownOption[];
  loadingMerchants: boolean;
  classes: Styles;
  match: match;
  renderSubmit: Function;
  merchantInitialValue?: string;
  whitelistReports?: string[];
  isExternalUser?: boolean;
}

const handleCheckboxClicked = (
  setReportList: (list: string[]) => void,
  reportList: string[],
  { meta: { batchNotAllowed = false } }: Report,
) => (
  _: React.MouseEvent<HTMLInputElement, MouseEvent>,
  { checked, name }: CheckboxProps,
) => {
  let reports = reportList;

  if (name === 'allReports') {
    reports = checked ? getAllReports(mock.reports) : [];
  } else if (
    !batchNotAllowed ||
    (batchNotAllowed && reports.length === 0) ||
    !checked
  ) {
    reports = checked
      ? concat<string>(reports, [name as string])
      : without<string>([name as string], reports);
  }

  setReportList(reports);
};

const cardLabel = (
  classes: Styles,
  image: string,
  { type, border }: Meta,
  text?: string,
) => (
  <label className={classes.checkboxLabel}>
    <div>
      {type && <div className={classes.typeLabel}>{type}</div>}
      <div className={classes.textLabel}>{text}</div>
    </div>

    <img src={image} className={classnames(classes.labelImage, { border })} />
  </label>
);

const renderCampaignStartDate = (
  merchant: Merchant,
  classes: Styles,
  isExternalUser = false,
) => (
  <Grid className={classes.campaignStartContainer}>
    <Grid.Column className={classes.campaignStartLabel}>
      Campaign Start Date:{' '}
      {moment.utc(merchant.firstLiveDate).format('MMM DD, YYYY')}
    </Grid.Column>

    {!isExternalUser && (
      <Grid.Column className={classes.statSig} width={16}>
        {prop('statSig', merchant) && <StatSig data={merchant.statSig} />}
      </Grid.Column>
    )}
  </Grid>
);

const renderReportPicker = (
  merchant: Merchant,
  reportList: string[] = [],
  setReportList: (list: string[]) => void,
  classes: Styles,
  whitelistReports?: string[],
) => (report: Report) => {
  const { key, text, image, meta } = report;

  if (
    !isValidReport(meta, merchant) ||
    (whitelistReports && !whitelistReports.includes(key)) ||
    (meta.merchantList && !meta.merchantList.includes(merchant.merchantName))
  ) {
    return null;
  }

  const checked = includes(key, reportList);

  return (
    <Card key={key}>
      <Card.Content className={classes.reportButton}>
        <Checkbox
          name={key}
          onClick={handleCheckboxClicked(setReportList, reportList, report)}
          checked={checked}
          label={cardLabel(classes, image, meta, text)}
        />
      </Card.Content>
    </Card>
  );
};

const getMerchant = (merchants: Merchant[], merchantId?: string) => {
  return find(propEq('merchantId', merchantId))(merchants);
};

interface FormProps {
  emailMe: boolean;
  merchantId: string;
}

interface ReportState {
  reportList: string[];
  merchantUuid?: string;
}

const initialReportState: ReportState = {
  reportList: [],
  merchantUuid: undefined,
};

const SESSION_STORAGE_KEY = 'report-form';

const ReportForm: FunctionComponent<Props> = ({
  merchants,
  merchantDropdownOptions,
  loadingMerchants,
  classes,
  match,
  renderSubmit,
  merchantInitialValue,
  whitelistReports,
  isExternalUser,
}) => {
  const { url } = match;

  const [isSubmitting, setSubmitting] = useState(false);
  const [reportState, setReportState] = useState(initialReportState);
  const [merchant, setMerchant] = useState<Merchant>();
  const methods = useForm<FormProps>({
    mode: 'onBlur',
    defaultValues: {
      emailMe: false,
      merchantId: merchantInitialValue,
    },
  });
  const { errors, handleSubmit, formState, control } = methods;

  useEffect(() => {
    const storedReportState = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (storedReportState) {
      const parsedState = JSON.parse(storedReportState) as ReportState;
      const { merchantUuid, reportList } = parsedState;
      if (merchantUuid && reportList.length !== 0) {
        setReportState(parsedState);
      }
    }
  }, []);

  useEffect(() => {
    const merchant: Merchant = getMerchant(merchants, reportState.merchantUuid);
    if (merchant) {
      setMerchant(merchant);
    }
  }, [merchants]);

  useEffect(() => {
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(reportState));
  }, [reportState]);

  const onSubmit = async ({ emailMe }: FormProps) => {
    let pathname = null;
    const { reportList } = reportState;
    if (reportList.length === 1) {
      pathname = `${url}/detail/${reportList[0]}`;
    } else if (!emailMe) {
      pathname = `${url}/batch-report`;
    }

    try {
      await renderSubmit({ reportList, merchant }, pathname);
    } catch (err) {
      console.error(err);
    }

    setSubmitting(false);
  };

  const onMerchantSelectChange = (
    _: SyntheticEvent<HTMLElement, Event>,
    { value }: any,
  ) => {
    setReportState({
      merchantUuid: value,
      reportList: [],
    });
    const merchant: Merchant = getMerchant(merchants, value);
    setMerchant(merchant);
  };

  const setReportList = (reportList: string[]) => {
    const { merchantUuid } = reportState;
    setReportState({
      reportList,
      merchantUuid,
    });
  };

  const { reportList, merchantUuid } = reportState;
  const selectedMerchantIdx = merchantDropdownOptions.findIndex(
    (option) => option.key === merchantUuid,
  );

  return (
    <FormProvider {...methods}>
      <Form
        onSubmit={handleSubmit(onSubmit)}
        className={classes.form}
        error={path(['date'], !!errors)}
      >
        {merchant && renderCampaignStartDate(merchant, classes, isExternalUser)}

        <Form.Group className={classes.spacing}>
          <Form.Select
            loading={loadingMerchants}
            label="Choose a Merchant"
            options={merchantDropdownOptions}
            onChange={onMerchantSelectChange}
            width={6}
            required
            search
            name="merchantId"
            placeholder="Select"
            value={
              merchantDropdownOptions[selectedMerchantIdx]
                ? merchantDropdownOptions[selectedMerchantIdx].value
                : undefined
            }
          />
        </Form.Group>

        <Header as="h5" className={classes.formHeader}>
          Select which reports to run
        </Header>

        {merchant && (
          <div className={classes.spacing}>
            <Card.Group itemsPerRow={2} centered>
              {map(
                renderReportPicker(
                  merchant,
                  reportList,
                  setReportList,
                  classes,
                  whitelistReports,
                ),
                mock.reports,
              )}
            </Card.Group>
          </div>
        )}

        <Form.Group inline>
          <Form.Field>
            <Button
              disabled={
                !formState.isValid ||
                reportList.length === 0 ||
                isSubmitting ||
                loadingMerchants
              }
              loading={isSubmitting}
              type="submit"
              className={classes.button}
            >
              Run Report
            </Button>
          </Form.Field>
          <Form.Field>
            <Controller
              name="emailMe"
              control={control}
              render={({ onChange, value }) => (
                <Form.Checkbox
                  toggle
                  checked={value}
                  label="Email Me"
                  name="emailMe"
                  onClick={(evt, data) => onChange(data.checked)}
                />
              )}
            ></Controller>
          </Form.Field>
        </Form.Group>
      </Form>
    </FormProvider>
  );
};

export default ReportForm;
